import { ScopeReview, ScopeReviewCheck, TodoItem } from "../types";

const PLACEHOLDER_CATEGORY = "Placeholder Text";
const CATEGORY_ORDER = [
  PLACEHOLDER_CATEGORY,
  "Service Language",
  "Subservice Language",
  "Structure",
  "LOE Sanity",
  "Content Quality",
];

const SPECIAL_WORDS: Record<string, string> = {
  sow: "SOW",
  loe: "LOE",
};

export const humanizeField = (key: string): string => {
  if (key === "out") return "Out of Scope";
  return key
    .split("_")
    .filter(Boolean)
    .map(
      (word) =>
        SPECIAL_WORDS[word] ?? word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");
};

export const placeholderLocation = (location: string): string => {
  const first = location.indexOf(":");
  const last = location.lastIndexOf(":");
  if (first === -1) return humanizeField(location);
  if (first === last) {
    return `${location.slice(first + 1)} › ${humanizeField(
      location.slice(0, first)
    )}`;
  }
  const name = location.slice(first + 1, last);
  const field = location.slice(last + 1);
  return `${name} › ${humanizeField(field)}`;
};

const isLanguageCategory = (category: string) =>
  category === "service_language" || category === "subservice_language";

export const checkLabel = (check: ScopeReviewCheck): string => {
  if (
    check.status === "failed" &&
    isLanguageCategory(check.category) &&
    check.field
  ) {
    const where =
      check.service && check.subservice
        ? `${check.service} › ${check.subservice}`
        : check.service ?? check.subservice;
    const missing = `${humanizeField(check.field)} is missing`;
    return where ? `${where}: ${missing}` : missing;
  }
  const text = check.detail ?? humanizeField(check.item);
  return check.service ? `${check.service}: ${text}` : text;
};

export const todoItems = (review: ScopeReview): TodoItem[] => {
  const placeholders: TodoItem[] = review.placeholder_warnings.map((w) => ({
    id: w.id,
    kind: "placeholder",
    category: PLACEHOLDER_CATEGORY,
    label: placeholderLocation(w.location),
    detail: w.text,
  }));
  const checks: TodoItem[] = review.checks
    .filter((c) => c.status !== "passed")
    .map((c) => ({
      id: c.id,
      kind: c.status === "failed" ? "failed" : "warning",
      category: humanizeField(c.category),
      label: checkLabel(c),
      detail: c.detail,
    }));
  return [...placeholders, ...checks];
};

export const todoGroups = (
  items: TodoItem[]
): { category: string; items: TodoItem[] }[] => {
  const groups: { category: string; items: TodoItem[] }[] = [];
  items.forEach((item) => {
    const existing = groups.find((g) => g.category === item.category);
    if (existing) existing.items.push(item);
    else groups.push({ category: item.category, items: [item] });
  });
  const rank = (category: string) => {
    const i = CATEGORY_ORDER.indexOf(category);
    return i === -1 ? CATEGORY_ORDER.length : i;
  };
  // Array.prototype.sort is stable, so unknown categories keep first-seen order.
  return groups.sort((a, b) => rank(a.category) - rank(b.category));
};

export const passedChecks = (review: ScopeReview): ScopeReviewCheck[] =>
  review.checks.filter((c) => c.status === "passed");

export const progress = (
  review: ScopeReview
): { done: number; total: number } => {
  const ids = new Set(todoItems(review).map((i) => i.id));
  const done = new Set(
    (review.completed_item_ids ?? []).filter((id) => ids.has(id))
  ).size;
  return { done, total: ids.size };
};
